import React, { useState } from "react";
import { FaPlus, FaMinus, FaInfoCircle, FaCheck } from "react-icons/fa";
import { Card, Input, Modal } from "../../ui";

const GAME_OPTIONS = [
  {
    key: "fadedQuestions",
    title: "نقاط مخفية",
    description:
      "تُخفى أرقام النقاط عن اللاعبين في شاشة اللعب. يظهر السؤال فارغاً بدون رقم، والأسئلة تُرتّب عشوائياً.",
    example: "المربعات تكون فارغة ❓ وتظهر النقاط فقط بعد الإجابة ✅",
  },
  {
    key: "lowToHighPoints",
    title: "من السهل للصعب",
    description:
      "تُقفل الأسئلة 🔒 ما عدا السؤال التالي. يجب الإجابة بالترتيب من الأسهل للأصعب.",
    example: "ابدأ بـ100، ثم يُفتح 200، ثم 300...",
  },
  {
    key: "from246PointsSystem",
    title: "نظام النقاط 2-4-6",
    description: "نظام نقاط بديل. كل فئة تحتوي على 6 أسئلة.",
    example: "2 × 200\n2 × 400\n2 × 600",
  },
  {
    key: "from135PointsSystem",
    title: "نظام النقاط 1-3-5",
    description: "نظام نقاط بديل. كل فئة تحتوي على 6 أسئلة.",
    example: "2 × 100\n2 × 300\n2 × 500",
  },
];

const TeamsEditor = ({ gameInfo, setGameInfo, teamImages }) => {
  const [infoOption, setInfoOption] = useState(null);
  const teams = gameInfo.teams || [];

  const setTeams = (next) => setGameInfo((prev) => ({ ...prev, teams: next }));

  const addTeam = () => {
    if (teams.length >= 4) return;
    setTeams([...teams, { name: "", image: teamImages?.[0]?._id }]);
  };
  const removeTeam = () => {
    if (teams.length <= 2) return;
    setTeams(teams.slice(0, -1));
  };
  const setTeamName = (i, name) =>
    setTeams(teams.map((t, idx) => (idx === i ? { ...t, name } : t)));
  const setTeamImage = (i, imageId) =>
    setTeams(teams.map((t, idx) => (idx === i ? { ...t, image: imageId } : t)));

  // 135 and 246 are mutually exclusive.
  const toggleOption = (key) => {
    setGameInfo((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      if (key === "from135PointsSystem" && next[key])
        next.from246PointsSystem = false;
      if (key === "from246PointsSystem" && next[key])
        next.from135PointsSystem = false;
      return next;
    });
  };

  const imgUrl = (id) => teamImages?.find((ti) => ti._id === id)?.image;

  const stepperClass =
    "w-10 h-10 rounded-xl bg-white/10 border border-white/15 text-white flex items-center justify-center hover:bg-white/15 disabled:opacity-30 disabled:cursor-not-allowed";

  return (
    <div>
      {/* Teams */}
      <div className="mb-10">
        <div className="flex items-center justify-between gap-4 mb-1">
          <h2 className="text-xl font-extrabold">الفِرَق</h2>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={removeTeam}
              disabled={teams.length <= 2}
              className={stepperClass}
              aria-label="إزالة فريق"
            >
              <FaMinus aria-hidden="true" />
            </button>
            <span className="text-primary text-xl font-extrabold w-8 text-center" aria-live="polite">
              {teams.length}
            </span>
            <button
              type="button"
              onClick={addTeam}
              disabled={teams.length >= 4}
              className={stepperClass}
              aria-label="إضافة فريق"
            >
              <FaPlus aria-hidden="true" />
            </button>
          </div>
        </div>
        <p className="text-white/55 text-sm mb-4">الحد الأدنى 2 والأقصى 4</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {teams.map((team, i) => (
            <Card key={i} pad="sm">
              <Input
                type="text"
                value={team.name}
                onChange={(e) => setTeamName(i, e.target.value)}
                placeholder={`الفريق ${i + 1}`}
                aria-label={`اسم الفريق ${i + 1}`}
                className="mb-4 text-center font-bold"
              />
              {imgUrl(team.image) && (
                <img
                  src={imgUrl(team.image)}
                  alt=""
                  className="w-16 h-16 rounded-full mx-auto mb-4 object-cover border-2 border-primary"
                />
              )}
              <div className="flex gap-2 overflow-x-auto pb-1">
                {teamImages?.map((ti) => (
                  <button
                    type="button"
                    key={ti._id}
                    onClick={() => setTeamImage(i, ti._id)}
                    aria-pressed={team.image === ti._id}
                    className={`shrink-0 rounded-full border-2 transition-colors ${
                      team.image === ti._id
                        ? "border-primary"
                        : "border-transparent hover:border-white/30"
                    }`}
                  >
                    <img
                      src={ti.image}
                      alt=""
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Options */}
      <div>
        <h2 className="text-xl font-extrabold mb-1">خيارات اللعبة</h2>
        <p className="text-white/55 text-sm mb-4">
          اختر خيارات إضافية لتخصيص تجربة اللعب
        </p>
        <div className="space-y-2">
          {GAME_OPTIONS.map((opt) => {
            const selected = !!gameInfo[opt.key];
            return (
              <div
                key={opt.key}
                onClick={() => toggleOption(opt.key)}
                className={`flex items-center justify-between gap-3 rounded-xl p-3.5 border cursor-pointer transition-colors ${
                  selected
                    ? "border-primary/50 bg-primary/10"
                    : "border-white/10 bg-white/[0.05] hover:border-white/20"
                }`}
              >
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setInfoOption(opt);
                    }}
                    className="-m-1 p-1 rounded-md text-white/50 hover:text-white"
                    aria-label={`عن ${opt.title}`}
                  >
                    <FaInfoCircle aria-hidden="true" />
                  </button>
                  <span
                    className={`font-semibold ${
                      selected ? "text-primary" : "text-white"
                    }`}
                  >
                    {opt.title}
                  </span>
                </div>
                <div
                  className={`w-6 h-6 shrink-0 rounded-md border-2 flex items-center justify-center ${
                    selected
                      ? "bg-primary border-primary"
                      : "border-white/40"
                  }`}
                >
                  {selected && (
                    <FaCheck className="text-header text-xs" aria-hidden="true" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {infoOption && (
        <Modal open onClose={() => setInfoOption(null)} title={infoOption.title}>
          <p className="text-white/70 leading-relaxed mb-4">
            {infoOption.description}
          </p>
          <div className="rounded-xl bg-white/[0.05] border border-white/10 p-3.5 text-white/85 text-sm whitespace-pre-line">
            {infoOption.example}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default TeamsEditor;
